import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ChatAnswer, ChatErrorCode, ChatMessage, ChatRequestError } from 'modules/chat/domain/interfaces';
import chatResource from 'modules/chat/domain/resources/chat-resource';
import { RootState } from 'store/store';

const PREFIX = 'chat';

type ChatThunkConfig = { rejectValue: ChatRequestError };

const STATUS_FALLBACK_CODES: Record<number, ChatErrorCode> = {
  403: 'FORBIDDEN',
  503: 'UNAVAILABLE',
};

class NoActiveTournamentError extends Error {}

const extractError = (error: unknown, prefix: string): ChatRequestError => {
  if (error instanceof NoActiveTournamentError) {
    return { code: 'NO_ACTIVE_TOURNAMENT' };
  }

  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown; code?: unknown; error?: unknown; remainingTokens?: unknown } | undefined;
    const httpStatus = error.response?.status;
    const bodyCode = typeof data?.code === 'number' ? data.code : undefined;
    const status =
      httpStatus ?? (data?.error === 'FEATURE_REQUIRED' ? 403 : undefined) ?? bodyCode;

    if (status === 429) {
      return { code: data?.remainingTokens !== undefined ? 'TOKEN_LIMIT' : 'RATE_LIMIT', status };
    }

    const serverMessage = typeof data?.message === 'string' ? data.message : undefined;
    const fallback = status ? STATUS_FALLBACK_CODES[status] : undefined;

    if (serverMessage) return { message: serverMessage, status };
    if (fallback) return { code: fallback, status };
  }

  // Текст исключения — для разработчика; пользователь увидит перевод кода по умолчанию из reducer-а.
  console.error(`${prefix} error:`, error);
  return {};
};

const getActiveTournamentId = (getState: () => unknown): number => {
  const { auth } = getState() as RootState;
  const activeTournamentId = auth.activeTournamentId;

  if (!activeTournamentId) {
    throw new NoActiveTournamentError('No active tournament');
  }

  return activeTournamentId;
};

export const sendChatMessage = createAsyncThunk<ChatAnswer, string, ChatThunkConfig>(
  `${PREFIX}/sendChatMessage`,
  async (message, { rejectWithValue, getState }) => {
    try {
      const tournamentId = getActiveTournamentId(getState);
      return await chatResource.sendMessage(tournamentId, {
        message,
        timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
      });
    } catch (error) {
      return rejectWithValue(extractError(error, `${PREFIX}/sendChatMessage`));
    }
  },
);

export const loadChatHistory = createAsyncThunk<ChatMessage[], void, ChatThunkConfig>(
  `${PREFIX}/loadChatHistory`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const tournamentId = getActiveTournamentId(getState);
      return await chatResource.getHistory(tournamentId);
    } catch (error) {
      return rejectWithValue(extractError(error, `${PREFIX}/loadChatHistory`));
    }
  },
);

export const clearChatHistory = createAsyncThunk<void, void, ChatThunkConfig>(
  `${PREFIX}/clearChatHistory`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const tournamentId = getActiveTournamentId(getState);
      await chatResource.clearHistory(tournamentId);
    } catch (error) {
      return rejectWithValue(extractError(error, `${PREFIX}/clearChatHistory`));
    }
  },
);
